import { useCallback, useEffect, useState } from 'react'
import { createFileRoute, Link, useParams } from '@tanstack/react-router'
import { useAuth } from '~/lib/auth'
import {
  getDppDetails,
  getDppEvents,
  getDppIndex,
  listUserDpps,
  sortEventsNewestFirst,
  type ArianeeEvent,
  type DppRecord,
  type PriceIndex,
} from '~/lib/dpp'
import { AppHeader } from '~/components/app-header'
import { RequireAuth } from '~/components/require-auth'
import { Button } from '~/components/ui/button'

export const Route = createFileRoute('/dpp/$id')({
  head: () => ({
    meta: [
      { title: 'Passport — Provenance' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: DppDetailPage,
})

function DppDetailPage() {
  return (
    <RequireAuth>
      <AppHeader />
      <DppDetail />
    </RequireAuth>
  )
}

type Tab = 'specification' | 'history' | 'index'

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'specification', label: 'Specification' },
  { key: 'history', label: 'History' },
  { key: 'index', label: 'Index' },
]

function fmtDate(value?: string | null): string {
  if (!value) return '—'
  return value.slice(0, 10).replace(/-/g, '.')
}

function DppDetail() {
  const { user } = useAuth()
  const { id } = useParams({ from: '/dpp/$id' })
  const [record, setRecord] = useState<DppRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState<Tab>('specification')

  const load = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError('')
    try {
      const list = await listUserDpps(user.email)
      const base = list.find((d) => d.id === id)
      if (!base) {
        setError('Passport not found.')
        setRecord(null)
        return
      }
      const full = await getDppDetails(
        base.brand,
        base.model,
        base.serialNumber,
      )
      setRecord({ ...base, ...full })
    } catch {
      setError('Could not load this passport.')
    } finally {
      setLoading(false)
    }
  }, [user, id])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) {
    return (
      <main className="mx-auto max-w-[960px] px-[var(--gutter)] py-16">
        <p className="py-20 text-center font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
          Loading…
        </p>
      </main>
    )
  }

  if (error || !record) {
    return (
      <main className="mx-auto flex max-w-[960px] flex-col items-center gap-5 px-[var(--gutter)] py-24">
        <p className="font-mono text-[12px] tracking-[0.06em] text-danger">
          {error || 'Passport not found.'}
        </p>
        <Link
          to="/dpp"
          className="font-mono text-[11px] tracking-[0.14em] uppercase text-accent hover:underline"
        >
          ← Back to my passports
        </Link>
      </main>
    )
  }

  const medias =
    record.arianee?.content?.medias?.filter((m) => m?.url) ?? []
  const images = medias.length
    ? medias.map((m) => m.url)
    : record.thumbnail?.url
      ? [record.thumbnail.url]
      : []

  return (
    <main className="mx-auto max-w-[960px] px-[var(--gutter)] py-10 sm:py-14">
      <Link
        to="/dpp"
        className="mb-8 inline-flex font-mono text-[11px] tracking-[0.14em] uppercase text-ink-mute transition-colors hover:text-ink"
      >
        ← My passports
      </Link>

      <Gallery images={images} alt={`${record.brand} ${record.model}`} />

      <header className="mt-8 border-b border-[var(--line)] pb-6">
        <div className="mb-2 font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
          S/N {record.serialNumber}
        </div>
        <h1 className="font-mono text-[clamp(28px,4vw,44px)] font-[500] leading-[1.05] text-ink">
          {record.brand}
        </h1>
        <p className="mt-1 text-[16px] text-ink-soft">{record.model}</p>
      </header>

      <div
        className="mt-6 flex flex-wrap gap-2 border-b border-[var(--line)]"
        role="tablist"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-3 font-mono text-[11px] tracking-[0.14em] uppercase transition-colors ${
              tab === t.key
                ? 'border-accent text-ink'
                : 'border-transparent text-ink-mute hover:text-ink-soft'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="py-8">
        {tab === 'specification' && <SpecificationTab record={record} />}
        {tab === 'history' && <HistoryTab record={record} />}
        {tab === 'index' && <IndexTab record={record} />}
      </div>
    </main>
  )
}

function Gallery({ images, alt }: { images: string[]; alt: string }) {
  const [active, setActive] = useState(0)

  if (images.length === 0) {
    return (
      <div className="flex aspect-[16/9] items-center justify-center border border-[var(--line)] bg-surface-2">
        <span className="watch-glyph" aria-hidden="true" />
      </div>
    )
  }

  const safe = Math.min(active, images.length - 1)

  return (
    <div>
      <div className="flex aspect-[16/9] items-center justify-center overflow-hidden border border-[var(--line)] bg-surface-2">
        <img
          src={images[safe]}
          alt={alt}
          className="h-full w-full object-contain"
        />
      </div>
      {images.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {images.map((src, i) => (
            <button
              key={src + i}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Image ${i + 1}`}
              className={`h-14 w-14 overflow-hidden border bg-surface-2 ${
                i === safe ? 'border-accent' : 'border-[var(--line)]'
              }`}
            >
              <img
                src={src}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function SpecificationTab({ record }: { record: DppRecord }) {
  const content = record.arianee?.content
  const rows: Array<{ k: string; v: string }> = [
    { k: 'Brand', v: record.brand },
    { k: 'Model', v: record.model },
    ...(record.reference ? [{ k: 'Reference', v: record.reference }] : []),
    { k: 'Serial', v: record.serialNumber },
    ...(record.department ? [{ k: 'Department', v: record.department }] : []),
    { k: 'Status', v: record.status },
    { k: 'Issued', v: fmtDate(record.createdAt) },
    ...(content?.name ? [{ k: 'Name', v: content.name }] : []),
    ...(content?.sku ? [{ k: 'SKU', v: content.sku }] : []),
    ...(record.arianeeTokenId
      ? [{ k: 'Token ID', v: record.arianeeTokenId }]
      : []),
  ]

  return (
    <div className="bracket-frame relative border border-[var(--line)] bg-surface">
      <span className="bk-bl" aria-hidden="true" />
      <span className="bk-br" aria-hidden="true" />
      <dl className="divide-y divide-[var(--line)]">
        {rows.map((r) => (
          <div key={r.k} className="grid grid-cols-[160px_1fr] gap-4 px-7 py-4">
            <dt className="font-mono text-[11px] tracking-[0.12em] uppercase text-ink-mute">
              {r.k}
            </dt>
            <dd className="break-words text-[14px] text-ink">{r.v}</dd>
          </div>
        ))}
      </dl>
      {content?.description && (
        <div className="border-t border-[var(--line)] px-7 py-5">
          <div className="mb-2 font-mono text-[11px] tracking-[0.12em] uppercase text-ink-mute">
            Description
          </div>
          <p className="text-[14px] leading-[1.6] text-ink-soft">
            {content.description}
          </p>
        </div>
      )}
    </div>
  )
}

function HistoryTab({ record }: { record: DppRecord }) {
  const [events, setEvents] = useState<ArianeeEvent[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await getDppEvents(
        record.brand,
        record.model,
        record.serialNumber,
      )
      setEvents(sortEventsNewestFirst(data))
    } catch {
      setError('Could not load history.')
    } finally {
      setLoading(false)
    }
  }, [record])

  useEffect(() => {
    void load()
  }, [load])

  if (loading)
    return (
      <p className="py-10 text-center font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
        Loading…
      </p>
    )

  if (error)
    return (
      <div className="flex flex-col items-center gap-4 py-10">
        <p className="font-mono text-[12px] text-danger">{error}</p>
        <Button variant="outline" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    )

  if (!events || events.length === 0)
    return (
      <p className="py-10 text-center font-mono text-[11px] tracking-[0.12em] uppercase text-ink-mute">
        No on-chain events yet
      </p>
    )

  return (
    <ol className="relative ml-2 border-l border-[var(--line)]">
      {events.map((e) => {
        const eventMedia = (e.content?.medias ?? []).filter(
          (m) => m?.url && (m.mediaType ?? 'picture') === 'picture',
        )
        return (
          <li key={e.id} className="relative pb-7 pl-7 last:pb-0">
            <span
              className={`absolute -left-[5px] top-[3px] h-[9px] w-[9px] ${
                e.pending ? 'bg-ink-mute' : 'bg-accent'
              }`}
              aria-hidden="true"
            />
            <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-ink-mute">
              {new Date(e.timestamp * 1000)
                .toISOString()
                .slice(0, 10)
                .replace(/-/g, '.')}
              {e.pending ? ' · Pending' : ''}
            </div>
            <div className="mt-1 font-mono text-[15px] font-[500] text-ink">
              {e.content?.title || e.content?.type}
            </div>
            {e.content?.description && (
              <p className="mt-1 text-[14px] leading-[1.55] text-ink-soft">
                {e.content.description}
              </p>
            )}
            {eventMedia.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {eventMedia.map((m, i) => (
                  <a
                    key={m.url + i}
                    href={m.url}
                    target="_blank"
                    rel="noreferrer"
                    className="h-20 w-20 overflow-hidden border border-[var(--line)] bg-surface-2"
                  >
                    <img
                      src={m.url}
                      alt={e.content?.title || 'Event media'}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </a>
                ))}
              </div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

function IndexTab({ record }: { record: DppRecord }) {
  const [data, setData] = useState<PriceIndex | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const reference = record.reference

  const load = useCallback(async () => {
    if (!reference) return
    setLoading(true)
    setError('')
    try {
      const res = await getDppIndex(record.brand, reference)
      setData(res)
    } catch {
      setError('Could not load price index.')
    } finally {
      setLoading(false)
    }
  }, [record.brand, reference])

  useEffect(() => {
    void load()
  }, [load])

  if (!reference)
    return (
      <p className="py-10 text-center font-mono text-[11px] tracking-[0.12em] uppercase text-ink-mute">
        Price index requires a reference number
      </p>
    )

  if (loading)
    return (
      <p className="py-10 text-center font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
        Loading…
      </p>
    )

  if (error)
    return (
      <div className="flex flex-col items-center gap-4 py-10">
        <p className="font-mono text-[12px] text-danger">{error}</p>
        <Button variant="outline" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    )

  const prices = data?.found ? (data.prices ?? []) : []

  if (prices.length === 0)
    return (
      <p className="py-10 text-center font-mono text-[11px] tracking-[0.12em] uppercase text-ink-mute">
        Price data not available
      </p>
    )

  const latest = prices[prices.length - 1]

  return (
    <div className="bracket-frame relative border border-[var(--line)] bg-surface p-7">
      <span className="bk-bl" aria-hidden="true" />
      <span className="bk-br" aria-hidden="true" />
      <div className="mb-1 font-mono text-[11px] tracking-[0.16em] uppercase text-ink-mute">
        Latest market price
      </div>
      <div className="font-mono text-[36px] font-[500] leading-[1] text-ink">
        ${Math.round(latest.price).toLocaleString('en-US')}
      </div>
      <div className="mt-2 font-mono text-[10px] tracking-[0.14em] uppercase text-ink-mute">
        as of {fmtDate(latest.date)}
      </div>
      <PriceChart prices={prices} />
    </div>
  )
}

function PriceChart({ prices }: { prices: { date: string; price: number }[] }) {
  const W = 640
  const H = 180
  const PAD = 8
  const values = prices.map((p) => p.price)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1

  const points = prices.map((p, i) => {
    const x = PAD + (i / Math.max(prices.length - 1, 1)) * (W - PAD * 2)
    const y = PAD + (1 - (p.price - min) / span) * (H - PAD * 2)
    return `${x.toFixed(1)},${y.toFixed(1)}`
  })

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="mt-6 w-full"
      preserveAspectRatio="none"
      role="img"
      aria-label="Price history chart"
    >
      <polyline
        points={points.join(' ')}
        fill="none"
        stroke="#d97868"
        strokeWidth="1.5"
      />
    </svg>
  )
}
